import IRepository from "data/IRepository";
import { isSuccessStatus } from "data/helpers/responseHelper";
import { Requirements_AdminState } from "data/endpoints";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import AlertWithButton from "features/shared/alerts/components/AlertWithButton";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import ContentDialogConfirm from "features/shared/components/contentDialog/ContentDialogConfirm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";
import { AlertType } from "features/shared/alerts/enums/alertType";
import { EmailRequirementState } from "../enums/emailRequirementState";

interface Props {
  requirementId: string;
  emailRequirementState?: string | null;
  onStateChanged: () => void;
}

const RequirementAdminActions = ({
  requirementId,
  emailRequirementState,
  onStateChanged,
}: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const repo = useInjection<IRepository<object>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // state
  const [alertMessage, setAlertMessage] = useState<{ text: string; type: AlertType } | null>(null);

  // helpers
  const changeState = async (newEmailRequirementState: EmailRequirementState) => {
    const response = await trackPromise(
      repo.post<ActionResultDTO<string>>({
        url: Requirements_AdminState,
        routeParams: getRouteParams("requirementId", requirementId),
        data: { newEmailRequirementState },
      })
    );

    if (isSuccessStatus(response)) {
      const validationMessages = response.data?.validationMessages ?? [];
      const firstMessage = validationMessages[0];

      if (!firstMessage) {
        setAlertMessage({
          text: tLabels("requirementAdminActions.resultSuccess"),
          type: AlertType.Success,
        });

        onStateChanged();
      } else if (firstMessage.type === "Warning") {
        setAlertMessage({
          text: tLabels("requirementAdminActions.resultConcurrent"),
          type: AlertType.Warning,
        });

        onStateChanged();
      } else {
        setAlertMessage({
          text: tLabels("requirementAdminActions.resultError"),
          type: AlertType.Danger,
        });
      }
    } else {
      setAlertMessage({
        text: tLabels("requirementAdminActions.resultError"),
        type: AlertType.Danger,
      });
    }
  };

  const renderConfirmMessage = (message: string): JSX.Element => {
    const paragraphs = message
      .split(/\r?\n\s*\r?\n/)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph.length > 0);

    return (
      <div className="text-left">
        {paragraphs.map((paragraph, index) => (
          <p
            key={index}
            className={index === paragraphs.length - 1 ? "mb-0" : undefined}
          >
            {paragraph}
          </p>
        ))}
      </div>
    );
  };

  const openConfirm = (
    newEmailRequirementState: EmailRequirementState,
    title: string,
    message: string
  ) => {
    dialogService.openDialog(title, () => (
      <ContentDialogConfirm
        renderMessage={() => renderConfirmMessage(message)}
        action={() => changeState(newEmailRequirementState)}
      />
    ));
  };

  // handlers
  const completeRequirementHandler = () => {
    openConfirm(
      EmailRequirementState.Completed,
      tLabels("requirementAdminActions.completeConfirmTitle"),
      tLabels("requirementAdminActions.completeConfirmMessage")
    );
  };

  const rejectRequirementHandler = () => {
    openConfirm(
      EmailRequirementState.Rejected,
      tLabels("requirementAdminActions.rejectConfirmTitle"),
      tLabels("requirementAdminActions.rejectConfirmMessage")
    );
  };

  // sub renders
  const renderActions = (): JSX.Element => {
    return (
      <Dropdown className="ml-sm-2 mt-2 mt-sm-0">
        <Dropdown.Toggle
          id="requirement-admin-actions-dropdown"
          size="sm"
          variant="secondary"
        >
          <span className="mr-2">
            <FontAwesomeIcon icon="cog" />
          </span>
          <span>
            {tLabels("requirementAdminActions.actions")}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item onClick={completeRequirementHandler}>
            <span className="mr-2 text-success">
              <FontAwesomeIcon
                icon="check"
                fixedWidth
              />
            </span>
            <span>
              {tLabels("requirementAdminActions.complete")}
            </span>
          </Dropdown.Item>

          <Dropdown.Item onClick={rejectRequirementHandler}>
            <span className="mr-2 text-danger">
              <FontAwesomeIcon
                icon="times"
                fixedWidth
              />
            </span>
            <span>
              {tLabels("requirementAdminActions.reject")}
            </span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  // render
  if (emailRequirementState !== EmailRequirementState.Submitted && !alertMessage) {
    return null;
  }

  return (
    <div className="mb-3">
      {emailRequirementState === EmailRequirementState.Submitted && (
        <AlertWithButton
          alertInfo={tLabels("requirementAdminActions.pendingInfo")}
          renderButton={renderActions}
        />
      )}

      {alertMessage && (
        <div className={emailRequirementState === EmailRequirementState.Submitted ? "mt-3" : ""}>
          <AlertWithButton
            alertInfo={alertMessage.text}
            alertType={alertMessage.type}
          />
        </div>
      )}
    </div>
  );
};

export default RequirementAdminActions;